import React, { useState } from "react";
import { Popconfirm } from "antd";

import { UpOutlined, DownOutlined } from "@ant-design/icons";
import { Floors, QuickformAttributes } from "@api/client";
import { useToggle } from "@hooks/use-toggle";
import { usePermissions } from "@hooks/use-permission";
import { DeleteButton } from "@components/delete-button";

import { HandleAttributeValueUpdate, HandleDeleteFloor, HandleDeleteModule } from "../types";
import * as S from "../quick-form.styled";
import { AddModuleValueForm } from "../add-module-value-form";
import { AttributeFloorValue } from "../attribute-floor-value";
import { CollapsibleTableRowIndustrial } from "../collapsedTableRows/collapsible-table-row-industrial";

interface TableRowProps {
  floor: Floors;
  parentAttributes: QuickformAttributes;
  onAttributeUpdate: HandleAttributeValueUpdate;
  onDeleteFloor: HandleDeleteFloor;
  onDeleteModule: HandleDeleteModule;
}

const arrowOutlinedStyles = { cursor: "pointer", marginRight: "5px", fontSize: "10px", alignSelf: "center" };

export const TableRowIndustrial = ({
  floor,
  parentAttributes,
  onAttributeUpdate,
  onDeleteFloor,
  onDeleteModule,
}: TableRowProps) => {
  const [showCollapsed, setShowCollapsed] = useState(false);
  const { state: showFloorPopconfirm, toggle: toggleFloorPopconfirm } = useToggle();
  const { hasPermission } = usePermissions();

  return (
    <>
      <S.TableRow>
        <S.TableCell
          style={{ display: "flex", width: "80px" }}
          onClick={() => setShowCollapsed(!showCollapsed)}
        >
          {showCollapsed ? (
            <UpOutlined style={arrowOutlinedStyles} />
          ) : (
            <DownOutlined style={arrowOutlinedStyles} />
          )}
          <span>{floor.floorsOriginal}</span>
        </S.TableCell>
        <S.TableCell>
          <AttributeFloorValue
            attributeId={floor.buildingName?.id}
            currentValue={floor.buildingName?.valueOriginal}
            parentAttributeId={parentAttributes.buildingName?.id}
            onUpdate={onAttributeUpdate}
            floorsOriginal={floor.floorsOriginal}
            groupKey={floor.buildingName?.groupKey}
          />
        </S.TableCell>
        <S.TableCell>
          <AttributeFloorValue
            attributeId={floor.availableSpace?.id}
            currentValue={floor.availableSpace?.valueOriginal}
            parentAttributeId={parentAttributes.availableSpace?.id}
            onUpdate={onAttributeUpdate}
            floorsOriginal={floor.floorsOriginal}
            groupKey={floor.availableSpace?.groupKey}
          />
        </S.TableCell>
        <S.TableCell>
          <AttributeFloorValue
            attributeId={floor.availability?.id}
            currentValue={floor.availability?.valueOriginal}
            parentAttributeId={parentAttributes.availability?.id}
            onUpdate={onAttributeUpdate}
            floorsOriginal={floor.floorsOriginal}
            groupKey={floor.availability?.groupKey}
          />
        </S.TableCell>
        <S.TableCell>
          <AttributeFloorValue
            attributeId={floor.clearHeight?.id}
            currentValue={floor.clearHeight?.valueOriginal}
            parentAttributeId={parentAttributes.clearHeight?.id}
            onUpdate={onAttributeUpdate}
            floorsOriginal={floor.floorsOriginal}
            groupKey={floor.clearHeight?.groupKey}
          />
        </S.TableCell>
        <S.TableCell>
          <AttributeFloorValue
            attributeId={floor.columnGrid?.id}
            currentValue={floor.columnGrid?.valueOriginal}
            parentAttributeId={parentAttributes.columnGrid?.id}
            onUpdate={onAttributeUpdate}
            floorsOriginal={floor.floorsOriginal}
            groupKey={floor.columnGrid?.groupKey}
          />
        </S.TableCell>
        <S.TableCell>
          <AttributeFloorValue
            attributeId={floor.floorLoadingCapacity?.id}
            currentValue={floor.floorLoadingCapacity?.valueOriginal}
            parentAttributeId={parentAttributes.floorLoadingCapacity?.id}
            onUpdate={onAttributeUpdate}
            floorsOriginal={floor.floorsOriginal}
            groupKey={floor.floorLoadingCapacity?.groupKey}
          />
        </S.TableCell>
        <S.TableCell>
          <AttributeFloorValue
            attributeId={floor.documentComments?.id}
            currentValue={floor.documentComments?.valueOriginal}
            parentAttributeId={parentAttributes.documentComments?.id}
            onUpdate={onAttributeUpdate}
            floorsOriginal={floor.floorsOriginal}
            groupKey={floor.documentComments?.groupKey}
          />
        </S.TableCell>
        <S.TableCell>
          <AttributeFloorValue
            attributeId={floor.internalComments?.id}
            currentValue={floor.internalComments?.valueOriginal}
            parentAttributeId={parentAttributes.internalComments?.id}
            onUpdate={onAttributeUpdate}
            floorsOriginal={floor.floorsOriginal}
            groupKey={floor.internalComments?.groupKey}
          />
        </S.TableCell>
        <S.TableCell>
          <Popconfirm
            title="Are you sure that you want to delete this floor?"
            visible={showFloorPopconfirm}
            onConfirm={() => {
              onDeleteFloor(floor.floorsOriginal);
              toggleFloorPopconfirm();
            }}
            onCancel={toggleFloorPopconfirm}
          >
            <DeleteButton onClick={toggleFloorPopconfirm} disabled={!hasPermission("DELETE_ATTRIBUTE")} />
          </Popconfirm>
        </S.TableCell>
      </S.TableRow>
      {showCollapsed &&
        floor.modules &&
        floor.modules.map((module) => {
          return (
            <CollapsibleTableRowIndustrial
              module={module}
              floor={floor}
              onDeleteModule={onDeleteModule}
              onAttributeUpdate={onAttributeUpdate}
            />
          );
        })}
      {showCollapsed && (
        // avoid stretching rows after list is collapsed
        <div style={{ height: "70px" }}>
          <AddModuleValueForm floorsOriginal={floor.floorsOriginal} />
        </div>
      )}
    </>
  );
};
